'use client';
import ProductNav from '@components/Product/Nav';
import Reviews from '@components/Bazaarvoice/Reviews';
import { ProductProvider } from '@context/ProductProvider';
import Seo from '@repo/ui/src/components/Seo';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { productViewed } from '@repo/ui/src/services/analytics';
import { productToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { track } from '@repo/ui/src/services/analytics/utils';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import * as React from 'react';
import ProductPage, { type ProductPageProps } from './_ProductPage';
import useProducts from '@hooks/product/useProducts';

interface ProductDetailClientProps {
  children?: React.ReactNode;
  [key: string]: any;
}

const ProductDetailClientBoundary = ({ children, ...pageProps }: ProductDetailClientProps) => {
  const { locale } = useCurrentUrl();
  const { products } = useProducts([pageProps]);
  const product = products?.[0];

  React.useEffect(() => {
    track(productViewed, productToAnalytics(product, locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const name = product?.shortTitleMain ?? product.title;
  const seo = Array.isArray(pageProps?.seo) ? pageProps?.seo[0] : pageProps.seo;

  return (
    <ProductProvider
      product={product}
      altProduct={pageProps.sizes?.[0]}
      addOns={pageProps.addOns}
      locale={locale}
    >
      <div>
        {!!seo && <Seo {...seo} jsonLD={{ type: 'product', product }} />}
        <ProductNav
          name={name}
          size={product?.shortTitlePrimary}
          isTierThree={pageProps.template === PDPTemplate.TierThree}
        />
        <ProductPage {...(pageProps as ProductPageProps)} />
        {children}
        <Reviews sku={product?.sku} type="product" />
      </div>
    </ProductProvider>
  );
};

export default ProductDetailClientBoundary;
